import { RxDocument } from 'rxdb'
import { Element, Node } from 'slate'
import { LOGGER, set, toSnakeCase } from '@cicero/data/utils'
import { Attr, BaseSchema, IQuery, ModelSchema } from './helpers'
import { AttributeValueSchema } from './AttributeValueSchema'
import { CommentThreadSchema } from './CommentThreadSchema'
import { PageEventSchema } from './PageEventSchema'
import { PageTemplateSchema } from './PageTemplateSchema'
import { UrlSchema } from './UrlSchema'
import { UserSchema } from './UserSchema'
import { mapFiltersToPageQuery, usePageFilterOptions, usePageFilterOptionsLookup, usePageFilters, usePagesGroupedByAttribute } from './hooks'
import { ContentCommands } from 'editor/commands'

@ModelSchema({
  name: 'page',
  description: 'the basic building block of content',
  version: 0,
  required: ['title'],
  // local: ['search_index'],
  indexes: ['template_id', 'created_at', 'updated_at'],
  associations: () => [
    { type: 'belongsTo', model: UrlSchema, key: 'owner', foreignKey: 'owner_id' },
    { type: 'belongsTo', model: UserSchema, key: 'created_by', foreignKey: 'created_by_id' },
    { type: 'belongsTo', model: PageSchema, key: 'parent', foreignKey: 'parent_id' },
    { type: 'belongsTo', model: PageTemplateSchema, key: 'template', foreignKey: 'template_id' },
    { type: 'hasMany', model: PageSchema, key: 'children' },
    { type: 'hasMany', model: AttributeValueSchema, key: 'attribute_values' },
    { type: 'hasMany', model: CommentThreadSchema, key: 'comment_threads' },
    { type: 'hasMany', model: PageEventSchema, key: 'events' },
    { type: 'hasOne', model: UserSchema, key: 'inbox_assignee', foreignKey: 'inbox_assignee_id' },
  ],
  lifecycle: {
    async afterCreate(store, doc: PageDoc) {
      if (doc.slug === undefined && store.session?.url) {
        doc.atomicSet('slug', PageSchema.generatePageSlug(store.session.url.path, toSnakeCase(doc.title)))
      }
      // const PageEvents = store.getModel(PageEventSchema)
      // const event = await PageEvents.CREATE({ type: PageEventType.page_create, page_id: doc.id }) // TODO Get current user ID here
      LOGGER.debug('JUST CREATED A PAGE', doc, store)
    },

    cachePeriod(schema, params) {
      return params?.selector?.id ? 15000 : 5 * 60000
    },

    preChange(doc: PageDoc) {
      doc = BaseSchema.LIFECYCLE.preChange(doc)
      let searchIndex = doc.title

      if (typeof doc.content === 'string') {
        doc.content = JSON.parse(doc.content || null) // TODO Better error handling here?
      }

      if (doc.content) {
        delete doc.main_image
        for (const [node] of ContentCommands.getSubElements({ children: doc.content })) {
          if (node.type === 'image' && node.url && !doc.main_image) {
            doc.main_image = node.url as string
          }
        }

        searchIndex += '\n'
        searchIndex += Node.string({ type: 'editor', children: doc.content })
      }

      doc.search_index = searchIndex
      return doc
    },

    async fetch(store, params) {
      const { selector, modifier } = params
      if (selector.slug) {
        const { response } = await store.API.GET(`/pages/${selector.slug}`, {}, modifier)
        return response.page
      }
    },

    async fetchQuery(store, params) {
      const { selector, modifier } = params
      const { response } = await store.API.GET('/pages', {}, { ...selector, ...modifier })
      return response.pages
    },
  },
})
export class PageSchema extends BaseSchema {
  static get HOOKS() {
    return {
      mapFiltersToPageQuery,
      usePageFilterOptions,
      usePageFilterOptionsLookup,
      usePageFilters,
      usePagesGroupedByAttribute,
    }
  }

  static QUERIES = {
    ...BaseSchema.QUERIES,
    parentless: { selector: { parent_id: { $exists: false } } },
    templates(template_ids: string[]) { return { selector: { template_id: { $in: template_ids } } } },
    search(search: string) {
      const q: IQuery = {
        selector: {},
        modifier: { attribute_values: true },
        sort: [{ 'updated_at': 'desc' }],
      }
      if (search) { set(q, 'selector.search_index', { $regex: new RegExp(`.*${search}.*`, 'i') }) }
      return q
    },
  }

  static DEFAULT_ICON = 'insert_drive_file'
  static DEFAULT_CONTENT: IDocumentContent[] = [
    { type: 'paragraph', children: [{ text: '' }] },
  ]
  static ICON_OPTIONS = [
    'account_box',
    'account_tree',
    'apps',
    'archive',
    'attachment',
    'bookmark',
    'chat_bubble',
    'check_circle',
    'dashboard',
    'data_usage',
    'equalizer',
    'email',
    'error',
    'explore',
    'extension',
    'face',
    'favorite',
    'flag',
    'flash_on',
    'group',
    'help',
    'insert_drive_file',
    'keyboard',
    'landscape',
    'list_alt',
    'location_on',
    'mail',
    'new_releases',
    'offline_bolt',
    'palette',
    'panorama',
    'poll',
    'public',
    'query_builder',
    'radio',
    'settings',
    'share',
    'star',
    'title',
    'warning',
    'work',
    'whatshot',
  ]

  static generatePageSlug(owner: string, id: string) { return `${owner}/${id}` }
  static getPageLink(page: PageDoc) { return page?.slug ? `/${page.slug}` : '' }

  @Attr({ type: 'string' }) title: string
  @Attr({ type: 'string' }) slug?: string
  @Attr({ type: 'boolean', default: true }) searchable?: boolean
  @Attr({ type: ['string', 'null'] }) icon?: string
  @Attr({ type: ['array', 'null'] }) content?: IDocumentContent[]

  @Attr({ type: ['string'], local: true }) search_index?: string
  @Attr({ type: 'string', local: true }) main_image?: string

  @Attr({ type: 'number' }) inbox_status?: PageInboxStatus
  @Attr({ type: 'string', format: 'date-time' }) inbox_date?: string
  inbox_assignee_id?: string

  parent_id?: string
  owner_id?: string
  template_id?: string
  created_by_id?: string
}

export type PageDoc = RxDocument<PageSchema>
export interface IDocumentContent extends Element {
  type?: string
}

export enum PageInboxStatus {
  'pending' = 1,
  'archived' = 2,
}
